import { z } from 'zod';

/** Clinic context for conditional scheduler validation. When provided with non-empty lists, those scheduler fields are required. */
export type SchedulingStepClinicContext = {
  primary_schedulers?: unknown[];
  secondary_schedulers?: unknown[];
} | null;

function hasPrimarySchedulers(ctx: SchedulingStepClinicContext): boolean {
  return (
    Array.isArray(ctx?.primary_schedulers) && ctx.primary_schedulers.length > 0
  );
}

function hasSecondarySchedulers(ctx: SchedulingStepClinicContext): boolean {
  return (
    Array.isArray(ctx?.secondary_schedulers) &&
    ctx.secondary_schedulers.length > 0
  );
}

export function getSchedulingStepFormSchema(
  clinicData: SchedulingStepClinicContext
) {
  const requirePrimaryScheduler = hasPrimarySchedulers(clinicData);
  const requireSecondaryScheduler = hasSecondarySchedulers(clinicData);

  // Shadcn/Radix `FormSelect` can momentarily emit `""` when its options list changes
  // (e.g. Visit type SOC → ROC swaps the Creation type options). That `""` would fail
  // `z.enum(...)` with the technical "Invalid option..." message. `.catch(null)` maps any
  // enum parse failure to `null`; the `superRefine` below re-emits friendly "required" messages.
  return z
    .object({
      kinnser_create_episode: z.boolean().nullable().optional(),
      roger_create_episode: z.boolean().nullable().optional(),

      // Push to EMR flags
      push_to_kinnser: z.boolean().nullable().optional(),
      push_to_roger: z.boolean().nullable().optional(),

      // Shared visit-type / creation-type / roc_date (stored in intake.fields).
      // Used by BOTH Kinnser and Roger scheduling flows.
      kinnser_visit_type: z
        .enum(['soc', 'roc'])
        .nullable()
        .catch(null)
        .optional(),
      kinnser_creation_type: z
        .enum(['new_admission', 'new_episode', 'current_episode'])
        .nullable()
        .catch(null)
        .optional(),
      roc_date: z.string().nullable().optional(),

      // Kinnser Episode and SOC fields
      kinnser_type_of_soc: z.string().nullable().optional(),
      kinnser_soc_date: z.string().nullable().optional(),
      kinnser_episode_start_date: z.string().nullable().optional(),

      // Kinnser Clinician assignment fields
      kinnser_clinician_id: z.string().nullable().optional(),
      kinnser_clinical_care_manager_id: z.string().nullable().optional(),
      kinnser_case_manager_id: z.string().nullable().optional(),
      kinnser_oasis_approver_id: z.string().nullable().optional(),
      kinnser_pt_approver_id: z.string().nullable().optional(),
      kinnser_ot_approver_id: z.string().nullable().optional(),
      kinnser_st_approver_id: z.string().nullable().optional(),

      // Kinnser Scheduler assignment fields
      kinnser_primary_scheduler_id: z.string().nullable().optional(),
      kinnser_secondary_scheduler_id: z.string().nullable().optional(),

      // Roger Episode and SOC fields
      roger_type_of_soc: z.string().nullable().optional(),
      soc_date: z.string().nullable().optional(),
      episode_start_date: z.string().nullable().optional(),

      // Roger Clinician assignment fields
      roger_clinician_id: z.string().nullable().optional(),
      roger_case_manager_id: z.string().nullable().optional(),

      // Not a form field; set when validating from intake (on load) so we can relax rules for new patients.
      is_new_patient: z.boolean().nullable().optional(),
    })
    .superRefine((data, ctx) => {
      // Access parse context when provided (via zodResolver). Used to:
      // - Relax certain validation rules when the intake has been confirmed
      //   as an existing patient (is_new_patient === false)
      // - Enforce date ordering rules relative to prior episode dates.
      // is_new_patient is also set on data when validating from intake (on load).
      type RefineCtx = { context?: Record<string, unknown> };
      const parseContext = (ctx as RefineCtx)?.context as
        | {
            isNewPatient?: boolean | null;
            kinnserDischargeDate?: string | null;
            kinnserStartOfCare?: string | null;
            priorEpisodeStartDate?: string | null;
          }
        | undefined;
      const isExistingPatient = parseContext?.isNewPatient === false;
      const kinnserDischargeDate = parseContext?.kinnserDischargeDate ?? null;
      const kinnserStartOfCare = parseContext?.kinnserStartOfCare ?? null;
      const priorEpisodeStartDate = parseContext?.priorEpisodeStartDate ?? null;

      const parseSafeDate = (value: string | null | undefined) => {
        if (!value) return null;
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
      };

      const isOnOrBefore = (
        value: string | null | undefined,
        comparison: string | null
      ) => {
        if (!value || !comparison) return false;
        const candidate = parseSafeDate(value);
        const compare = parseSafeDate(comparison);
        if (!candidate || !compare) return false;
        return candidate <= compare;
      };

      // For existing patients with prior Kinnser episode context, ensure that
      // any new episode/scheduling start dates occur strictly AFTER:
      // - the discharge date (cannot be on or before),
      // - the start of care date (must be at least one day after), and
      // - the prior episode's start date (episode start must be after prior episode start).
      if (
        isExistingPatient &&
        (kinnserDischargeDate || kinnserStartOfCare || priorEpisodeStartDate)
      ) {
        const dateFields: Array<{
          key:
            | 'kinnser_soc_date'
            | 'kinnser_episode_start_date'
            | 'episode_start_date';
          label: string;
        }> = [
          { key: 'kinnser_soc_date', label: 'Start of Care Date' },
          { key: 'kinnser_episode_start_date', label: 'Episode Start Date' },
          { key: 'episode_start_date', label: 'Episode Start Date' },
        ];

        for (const { key, label } of dateFields) {
          const value = data[key];

          if (
            kinnserDischargeDate &&
            isOnOrBefore(value, kinnserDischargeDate)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be after the discharge date`,
              path: [key],
            });
            continue;
          }

          if (kinnserStartOfCare && isOnOrBefore(value, kinnserStartOfCare)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be at least one day after the start of care date`,
              path: [key],
            });
          }

          // Kinnser episode start date must be after the prior episode's start date (Roger has no such restriction).
          if (
            priorEpisodeStartDate &&
            key === 'kinnser_episode_start_date' &&
            isOnOrBefore(value, priorEpisodeStartDate)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be after the prior episode's start date`,
              path: [key],
            });
          }
        }

        // For existing patients with SOC, require Clinician (Kinnser does not
        // expose Type of SOC for existing-patient admissions; UI defaults to SN.)
        if (
          data.push_to_kinnser === true &&
          data.kinnser_visit_type === 'soc'
        ) {
          if (!data.kinnser_clinician_id) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Clinician is required',
              path: ['kinnser_clinician_id'],
            });
          }
        }

        // For existing patients, we only enforce the discharge/start-of-care/prior-episode
        // ordering rules and SOC-specific fields above. Other scheduling validations
        // are skipped so the step does not block intake completion.
        return;
      }

      // Visit type / creation type / ROC date are stored as SHARED keys in
      // intake.fields (`kinnser_visit_type`, `kinnser_creation_type`, `roc_date`)
      // and validated once for whichever EMR scheduling panel is active.
      const schedulingVisitTypeRequired =
        isExistingPatient &&
        (data.push_to_kinnser === true || data.roger_create_episode === true);
      if (schedulingVisitTypeRequired) {
        if (!data.kinnser_visit_type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Visit type is required',
            path: ['kinnser_visit_type'],
          });
        }
        if (!data.kinnser_creation_type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Creation type is required',
            path: ['kinnser_creation_type'],
          });
        }
        // Creation type must match visit type: SOC → new_admission | new_episode; ROC → current_episode
        if (data.kinnser_visit_type === 'soc' && data.kinnser_creation_type) {
          if (
            data.kinnser_creation_type !== 'new_admission' &&
            data.kinnser_creation_type !== 'new_episode'
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                'For SOC, creation type must be New admission or New episode',
              path: ['kinnser_creation_type'],
            });
          }
        }
        if (data.kinnser_visit_type === 'roc' && data.kinnser_creation_type) {
          if (data.kinnser_creation_type !== 'current_episode') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'For ROC, creation type must be Current episode',
              path: ['kinnser_creation_type'],
            });
          }
        }
        // When ROC is selected, require roc_date. It must be after the matched
        // episode's start date for both Kinnser and Roger scheduling.
        if (data.kinnser_visit_type === 'roc') {
          if (!data.roc_date || data.roc_date.trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'ROC date is required',
              path: ['roc_date'],
            });
          } else if (priorEpisodeStartDate) {
            const rocDate = parseSafeDate(data.roc_date);
            const minRoc = parseSafeDate(priorEpisodeStartDate);
            if (rocDate && minRoc && rocDate <= minRoc) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  "ROC date must be after the matched episode's start date",
                path: ['roc_date'],
              });
            }
          }
        }
      }

      if (data.kinnser_create_episode === true) {
        if (
          !data.kinnser_clinician_id ||
          data.kinnser_clinician_id.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Clinician is required',
            path: ['kinnser_clinician_id'],
          });
        }
        if (
          !data.kinnser_clinical_care_manager_id ||
          data.kinnser_clinical_care_manager_id.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Clinical Care Manager is required',
            path: ['kinnser_clinical_care_manager_id'],
          });
        }
        if (
          !data.kinnser_case_manager_id ||
          data.kinnser_case_manager_id.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Case Manager is required',
            path: ['kinnser_case_manager_id'],
          });
        }
        if (
          !data.kinnser_oasis_approver_id ||
          data.kinnser_oasis_approver_id.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'OASIS Approver is required',
            path: ['kinnser_oasis_approver_id'],
          });
        }
        if (
          !data.kinnser_pt_approver_id ||
          data.kinnser_pt_approver_id.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'PT Approver is required',
            path: ['kinnser_pt_approver_id'],
          });
        }
        if (
          !data.kinnser_ot_approver_id ||
          data.kinnser_ot_approver_id.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'OT Approver is required',
            path: ['kinnser_ot_approver_id'],
          });
        }
        if (
          !data.kinnser_st_approver_id ||
          data.kinnser_st_approver_id.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'ST Approver is required',
            path: ['kinnser_st_approver_id'],
          });
        }
        if (requirePrimaryScheduler) {
          if (
            !data.kinnser_primary_scheduler_id ||
            data.kinnser_primary_scheduler_id.length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Primary scheduler is required',
              path: ['kinnser_primary_scheduler_id'],
            });
          }
        }
        if (requireSecondaryScheduler) {
          if (
            !data.kinnser_secondary_scheduler_id ||
            data.kinnser_secondary_scheduler_id.length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Secondary scheduler is required',
              path: ['kinnser_secondary_scheduler_id'],
            });
          }
        }
        if (
          !data.kinnser_type_of_soc ||
          data.kinnser_type_of_soc.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Type of SOC is required',
            path: ['kinnser_type_of_soc'],
          });
        }
        if (!data.kinnser_soc_date || data.kinnser_soc_date.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Start of Care Date is required',
            path: ['kinnser_soc_date'],
          });
        }
        if (
          !data.kinnser_episode_start_date ||
          data.kinnser_episode_start_date.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Episode Start Date is required',
            path: ['kinnser_episode_start_date'],
          });
        }
        // Episode Start Date cannot be before SOC date
        if (data.kinnser_soc_date && data.kinnser_episode_start_date) {
          const socDate = parseSafeDate(data.kinnser_soc_date);
          const episodeStart = parseSafeDate(data.kinnser_episode_start_date);
          if (socDate && episodeStart && episodeStart < socDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                'Episode Start Date cannot be prior to the Start of Care date',
              path: ['kinnser_episode_start_date'],
            });
          }
        }
      }

      // Episode fields are required when Roger Create Episode is enabled (new vs existing matches RogerSchedulingStepFormContent).
      if (data.roger_create_episode === true) {
        const rogerNeedsStaffFields = !isExistingPatient;
        const isRogerRocPath = data.kinnser_visit_type === 'roc';

        if (rogerNeedsStaffFields) {
          if (
            !data.roger_clinician_id ||
            data.roger_clinician_id.length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Clinician is required',
              path: ['roger_clinician_id'],
            });
          }
          if (
            !data.roger_case_manager_id ||
            data.roger_case_manager_id.length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Case Manager is required',
              path: ['roger_case_manager_id'],
            });
          }
        }

        // `roger_type_of_soc` = SN/PT/OT/ST. Existing patient + Roger SOC/ROC: field hidden
        // schedule_roger_existing_patient defaults to SN on the server (Kinnser parity).
        // Visit type is stored on the shared `kinnser_visit_type` key.
        const rogerSocRocExisting =
          isExistingPatient &&
          (data.kinnser_visit_type === 'roc' ||
            data.kinnser_visit_type === 'soc');
        if (
          !rogerSocRocExisting &&
          (!data.roger_type_of_soc || data.roger_type_of_soc.length === 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Type of SOC is required',
            path: ['roger_type_of_soc'],
          });
        }

        if (!isRogerRocPath) {
          if (!data.soc_date || data.soc_date.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Start of Care Date is required',
              path: ['soc_date'],
            });
          }
          if (
            !data.episode_start_date ||
            data.episode_start_date.length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Episode Start Date is required',
              path: ['episode_start_date'],
            });
          }
          if (data.soc_date && data.episode_start_date) {
            const socDate = parseSafeDate(data.soc_date);
            const episodeStart = parseSafeDate(data.episode_start_date);
            if (socDate && episodeStart && episodeStart < socDate) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'Episode Start Date cannot be prior to the Start of Care date',
                path: ['episode_start_date'],
              });
            }
          }
        }
      }
    });
}

/** Default schema with no clinic context (scheduler fields not required). Use getSchedulingStepFormSchema(clinicData) when clinic data is available. */
export const schedulingStepFormSchema = getSchedulingStepFormSchema(null);

export type SchedulingStepFormData = z.infer<typeof schedulingStepFormSchema>;
